// src/utils/services/preventiveAiService.js
import { callOpenAIChat } from './openaiClient';

// 🔧 Helper: extrae el JSON aunque venga con ```json ... ``` o texto extra
const safeParseJsonFromModel = (rawContent) => {
  if (!rawContent) {
    throw new Error('No content to parse');
  }

  let text = Array.isArray(rawContent)
    ? rawContent
        .map((p) =>
          typeof p === 'string'
            ? p
            : (p && (p.text || p.content || '')) || ''
        )
        .join('')
    : String(rawContent);

  text = text.trim();

  if (text.startsWith('```')) {
    text = text.replace(/^```[a-zA-Z]*\s*/m, '');
    text = text.replace(/```$/m, '');
    text = text.trim();
  }

  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    console.error('[safeParseJsonFromModel] No JSON object found in text:', text);
    throw new Error('No JSON object found in model response');
  }

  const jsonSlice = text.slice(firstBrace, lastBrace + 1);
  return JSON.parse(jsonSlice);
};

export const generatePreventiveServicesFromAI = async ({
  preventiveContextText,
}) => {
  const contextText = preventiveContextText || '';

  if (!contextText.trim()) {
    console.warn('[generatePreventiveServicesFromAI] Empty preventive context');
    return [];
  }

  const userPrompt = `
You are a primary-care physician and preventive-care specialist who follows USPSTF recommendations.

I will provide you with a consolidated clinical context of ONE adult patient:
- Demographics (age, sex, DOB, language)
- Vitals (BP, BMI)
- Clinical notes (AWV, follow up, telehealth, hospital discharge)
- Medications
- Laboratory results
- Allergies

Your task is to determine which USPSTF-like preventive services are:
- Applicable for this patient (based on age, sex, risk factors and diagnoses).
- Currently CLOSED (up to date) or OPEN (due or overdue).
- NotApplicable (not recommended or out of scope).

Return ONLY a JSON object with the following structure (no extra text):

{
  "services": [
    {
      "id": "CRC_SCREENING",
      "name": "Colorectal Cancer Screening",
      "grade": "A",
      "status": "Closed",        // "Open" | "Closed" | "NotApplicable"
      "lastPerformed": "YYYY-MM-DD or 'N/A'",
      "nextDue": "YYYY-MM-DD or 'N/A'",
      "rationale": "Short explanation in English of why you chose this status and due date"
    }
  ]
}

Guidance:
- Use USPSTF adult preventive-care concepts (e.g. colorectal cancer screening, breast cancer screening, cervical cancer screening, depression screening, tobacco cessation counseling, etc.).
- "Applicable" depends on age, sex and risk factors (e.g. smoking, diabetes, cardiovascular disease, family history).
- "Closed" means the preventive service appears up to date based on tests or visits in the context (for example a colonoscopy within the last 10 years for average-risk CRC screening).
- "Open" means the patient meets criteria for the service but you do NOT see recent qualifying evidence in the record, or the last evidence is outdated.
- "NotApplicable" means the service is not indicated (for example outside age range or clearly not relevant).
- Use dates found in labs, vitals and notes; if unsure, use 'N/A' and explain in the rationale.
- Always consider at least these candidate services WHEN they are applicable:
  - Colorectal Cancer Screening
  - Breast Cancer Screening (if applicable)
  - Cervical Cancer Screening (if applicable)
  - Depression Screening
  - Diabetes screening / A1c
  - Hypertension screening / BP control
  - Lipid screening for cardiovascular risk
  - Tobacco use and cessation counseling (if smoker)

===== PATIENT CONTEXT FOR USPSTF PREVENTIVE EVALUATION =====

${contextText}
`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [{ role: 'user', content: userPrompt }],
    temperature: 0.2,
  });

  const content = data?.choices?.[0]?.message?.content;
  console.log('[generatePreventiveServicesFromAI] RAW content from OpenAI:', content);

  if (!content) {
    console.error('[generatePreventiveServicesFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJsonFromModel(content);

    if (!json.services || !Array.isArray(json.services)) {
      console.error(
        '[generatePreventiveServicesFromAI] Unexpected JSON format:',
        json
      );
      throw new Error('Unexpected JSON format (missing "services")');
    }

    // Normalizamos campos para el frontend
    return json.services.map((s) => ({
      id: s.id || '',
      name: s.name || s.service || '',
      grade: s.grade || s.usptfGrade || '',
      status: s.status || 'Open',
      lastPerformed: s.lastPerformed || 'N/A',
      nextDue: s.nextDue || 'N/A',
      rationale: s.rationale || s.reason || '',
    }));
  } catch (err) {
    console.error('[generatePreventiveServicesFromAI] Error parsing JSON:', err);
    return [];
  }
};
